import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { I18nService } from 'i18n';
import { Logger } from 'shared';
import { AuthService } from '../services/auth.service';

/**
 * Keeps users the backend has excluded from the Community Network off its routes.
 *
 * Reads `community_enabled` from the user in auth state (it rides on the bootstrap
 * payload, like `legal_acknowledgement_required`), because guards cannot await.
 *
 * Fails open by design: only an explicit `false` redirects. A backend that predates
 * the flag omits it, and the community API enforces membership on every request
 * anyway (403 `community_not_member`) — this guard is a UX affordance, not the gate.
 */
export const communityAccessGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const i18nService = inject(I18nService);
  const logger = new Logger('communityAccessGuard');

  return authService.getUser$().pipe(
    take(1),
    map(user => {
      if (!user) {
        logger.debug('NO USER => REDIRECT TO /login');
        return router.createUrlTree(['/login']);
      }

      if (user.community_enabled !== false) {
        return true;
      }

      logger.debug(`COMMUNITY DISABLED FOR USER => DASHBOARD (from ${state.url})`);
      return router.createUrlTree([`/${i18nService.getLanguage()}/dashboard`]);
    })
  );
};
